import { ref, type Ref } from 'vue';

export interface CorrecaoPendente {
  id: number;
  interacaoID?: number | null;
  sistema: string;
  baseCliente: string;
  plataforma: string;
  usuarioID: number;
  usuarioNome?: string | null;
  controllerContexto?: string | null;
  acaoContexto?: string | null;
  perguntaOriginal: string;
  respostaOriginal?: string | null;
  correcaoSugerida: string;
  status: 'pendente' | 'aprovada' | 'rejeitada';
  chunkGeradoID?: number | null;
  revisadoEm?: string | null;
  revisadoPorUsuarioID?: number | null;
  observacaoRevisao?: string | null;
  criadoEm: string;
}

export interface UseAdminOptions {
  apiBaseUrl: string;
  usuarioTipoID: number;
  revisorUsuarioID: number;
}

export interface UseAdmin {
  correcoes: Ref<CorrecaoPendente[]>;
  carregando: Ref<boolean>;
  erro: Ref<string | null>;
  filtroStatus: Ref<'pendente' | 'aprovada' | 'rejeitada' | 'todos'>;
  carregar(): Promise<void>;
  aprovar(id: number, tituloChunk?: string, observacao?: string): Promise<void>;
  rejeitar(id: number, observacao?: string): Promise<void>;
  editar(id: number, correcaoNova: string, observacao?: string): Promise<void>;
  uploadDocumento(arquivo: File): Promise<UploadResultado>;
}

export interface UploadResultado {
  fileID: string;
  nomeArquivo: string;
  mimeType: string;
  chunksGerados: number;
  usouFallbackOpenXml: boolean;
  erro?: string | null;
}

export function useAdmin(options: UseAdminOptions): UseAdmin {
  const correcoes = ref<CorrecaoPendente[]>([]);
  const carregando = ref(false);
  const erro = ref<string | null>(null);
  const filtroStatus = ref<UseAdmin['filtroStatus']['value']>('todos');

  const url = (path: string) => `${options.apiBaseUrl.replace(/\/$/, '')}${path}`;
  const headers = () => ({
    'Content-Type': 'application/json',
    'X-Usuario-Tipo-Id': String(options.usuarioTipoID),
    'X-Usuario-Id': String(options.revisorUsuarioID)
  });

  async function carregar() {
    carregando.value = true;
    erro.value = null;
    try {
      const status = filtroStatus.value === 'todos' ? '' : `?status=${filtroStatus.value}`;
      const resp = await fetch(url(`/api/assistenteia/admin/correcoes${status}`), {
        method: 'GET',
        headers: headers()
      });
      if (!resp.ok) {
        if (resp.status === 403) {
          erro.value = 'Acesso negado: este painel é restrito a usuários de suporte (TipoID = -7).';
          return;
        }
        const t = await resp.text().catch(() => '');
        erro.value = `Erro ${resp.status}: ${t}`;
        return;
      }
      correcoes.value = (await resp.json()) as CorrecaoPendente[];
    } catch (e) {
      erro.value = e instanceof Error ? e.message : 'Erro desconhecido';
    } finally {
      carregando.value = false;
    }
  }

  async function aprovar(id: number, tituloChunk?: string, observacao?: string) {
    const resp = await fetch(url(`/api/assistenteia/admin/correcoes/${id}/aprovar`), {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({
        revisadoPorUsuarioID: options.revisorUsuarioID,
        tituloChunk: tituloChunk ?? null,
        observacaoRevisao: observacao ?? null
      })
    });
    if (!resp.ok) {
      const t = await resp.text().catch(() => '');
      throw new Error(`Erro ${resp.status}: ${t}`);
    }
    await carregar();
  }

  async function rejeitar(id: number, observacao?: string) {
    const resp = await fetch(url(`/api/assistenteia/admin/correcoes/${id}/rejeitar`), {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({
        revisadoPorUsuarioID: options.revisorUsuarioID,
        observacaoRevisao: observacao ?? null
      })
    });
    if (!resp.ok) {
      const t = await resp.text().catch(() => '');
      throw new Error(`Erro ${resp.status}: ${t}`);
    }
    await carregar();
  }

  async function editar(id: number, correcaoNova: string, observacao?: string) {
    const resp = await fetch(url(`/api/assistenteia/admin/correcoes/${id}/editar`), {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({
        editadoPorUsuarioID: options.revisorUsuarioID,
        correcaoNova,
        observacaoRevisao: observacao ?? null
      })
    });
    if (!resp.ok) {
      const t = await resp.text().catch(() => '');
      throw new Error(`Erro ${resp.status}: ${t}`);
    }
    await carregar();
  }

  async function uploadDocumento(arquivo: File): Promise<UploadResultado> {
    const fd = new FormData();
    fd.append('arquivo', arquivo);

    // Importante: NAO setar Content-Type manualmente — o browser inclui o boundary
    // do multipart automaticamente quando o body e' FormData.
    const headersUpload: Record<string, string> = {
      'X-Usuario-Tipo-Id': String(options.usuarioTipoID),
    'X-Usuario-Id': String(options.revisorUsuarioID)
    };

    const resp = await fetch(url('/api/assistenteia/admin/documentos/upload'), {
      method: 'POST',
      headers: headersUpload,
      body: fd
    });

    if (!resp.ok) {
      const t = await resp.text().catch(() => '');
      throw new Error(`Erro ${resp.status}: ${t}`);
    }
    return (await resp.json()) as UploadResultado;
  }

  return { correcoes, carregando, erro, filtroStatus, carregar, aprovar, rejeitar, editar, uploadDocumento };
}
